import { useDroppable, useDndMonitor, useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useState, type ReactNode } from 'react';
import {
  CalendarClock,
  CalendarDays,
  CircleSlash,
  Hourglass,
} from 'lucide-react';
import { brandColors } from '@/design';
import type { DayFocus, TaskPlan, TodoProject, TodoSwimLane } from '../../types';
import { TODO_PROJECT_COLOR_TOKENS } from '../../utils/todoSwimLanes';

/**
 * Bidirectional Schedule chips that sit above the Projects pivot.
 *
 *  - 3 lane zones (Today / This week / Later) accept drops:
 *    - From a project Kanban column → schedules the card (sets `swimLane`)
 *    - From another lane chip below → moves between lanes
 *  - 1 Unschedule zone (the 4th tile, only really visible while dragging)
 *    accepts drops to clear `swimLane` so the card lives only in its project
 *  - Each lane zone shows its currently scheduled cards as small draggable
 *    chips so users can grab them and move them around without going to the
 *    Schedule view (option B from the Todos chat)
 *
 * Drag IDs:
 *  - Project-column cards use the existing `ws-drag:task:` / `ws-drag:focus:`
 *    convention; chips inside zones use `ws-drag-zone:task:` / `ws-drag-zone:focus:`
 *    so dnd-kit doesn't see two registrations with the same id when a card is
 *    visible in both places. The parent's `parseWorkspaceDragId` handles both.
 *
 * Drop IDs:
 *  - Lane zones reuse `ws-list-lane:${lane}` so the existing handler works
 *  - Unschedule zone is `ws-unschedule`
 */

export interface ScheduleDropZonesProps {
  /** Active card counts per lane (rendered as a small chip on each zone). */
  laneCounts?: Record<TodoSwimLane, number>;
  /** Cards currently scheduled in each lane, surfaced as draggable mini-chips. */
  rowsByLane?: Record<TodoSwimLane, ScheduleZoneRow[]>;
  /** Project lookup so chips can show their project's accent color. */
  projectsById?: Map<string, TodoProject>;
}

export type ScheduleZoneRow =
  | { kind: 'task'; id: string; task: TaskPlan }
  | { kind: 'day_focus'; id: string; focus: DayFocus; task: TaskPlan };

/** How many chips to show inline before collapsing into "+N more". */
const CHIPS_PER_ZONE = 3;

const LANE_META: Array<{
  id: TodoSwimLane;
  label: string;
  hint: string;
  icon: ReactNode;
}> = [
  {
    id: 'today',
    label: 'Today',
    hint: 'Doing this today',
    icon: <CalendarClock size={16} />,
  },
  {
    id: 'this_week',
    label: 'This week',
    hint: 'Plan to finish this week',
    icon: <CalendarDays size={16} />,
  },
  {
    id: 'later',
    label: 'Later',
    hint: 'Backlog — no commitment yet',
    icon: <Hourglass size={16} />,
  },
];

export function ScheduleDropZones({
  laneCounts,
  rowsByLane,
  projectsById,
}: ScheduleDropZonesProps) {
  // Track whether a workspace card is currently mid-drag so the zones (and
  // the Unschedule tile) can light up. We can't use `useDroppable.active`
  // for layout-level signal because it only flips on hover.
  const [dragging, setDragging] = useState(false);
  useDndMonitor({
    onDragStart: () => setDragging(true),
    onDragCancel: () => setDragging(false),
    onDragEnd: () => setDragging(false),
  });

  return (
    <div aria-label="Schedule drop zones" className="flex flex-col gap-2">
      <div className="flex items-baseline gap-2 px-1">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
          Plan into your week
        </p>
        <p className="text-[11px] text-[var(--color-text-muted)] hidden sm:inline">
          · drag a card here to schedule, drag a chip onto Unschedule to clear
        </p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        {LANE_META.map((lane) => (
          <ScheduleDropZone
            key={lane.id}
            lane={lane}
            count={laneCounts?.[lane.id] ?? 0}
            chips={rowsByLane?.[lane.id] ?? []}
            dragging={dragging}
            projectsById={projectsById}
          />
        ))}
        <UnscheduleZone dragging={dragging} />
      </div>
    </div>
  );
}

function ScheduleDropZone({
  lane,
  count,
  chips,
  dragging,
  projectsById,
}: {
  lane: typeof LANE_META[number];
  count: number;
  chips: ScheduleZoneRow[];
  dragging: boolean;
  projectsById?: Map<string, TodoProject>;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `ws-list-lane:${lane.id}`,
  });

  // See module-level comment for the visual stack rationale.
  let background = 'var(--color-surface)';
  let borderColor: string = 'var(--color-border)';
  let borderStyle: 'solid' | 'dashed' = 'solid';
  let foreground = 'var(--color-text-secondary)';
  let shadow = 'none';

  if (isOver) {
    background = `${brandColors.lightBlue}1F`;
    borderColor = brandColors.lightBlue;
    borderStyle = 'dashed';
    foreground = brandColors.lightBlue;
    shadow = `0 4px 14px ${brandColors.lightBlue}33`;
  } else if (dragging) {
    background = 'var(--color-surface-elevated)';
    borderColor = `${brandColors.lightBlue}66`;
    borderStyle = 'dashed';
    foreground = 'var(--color-text-primary)';
  }

  const visibleChips = chips.slice(0, CHIPS_PER_ZONE);
  const hiddenCount = Math.max(0, chips.length - visibleChips.length);

  return (
    <div
      ref={setNodeRef}
      role="region"
      aria-label={`Drop here to schedule for ${lane.label}`}
      className="flex flex-col gap-2 px-3 py-2.5 rounded-xl transition-all"
      style={{
        background,
        border: `1.5px ${borderStyle} ${borderColor}`,
        color: foreground,
        boxShadow: shadow,
        transform: isOver ? 'translateY(-1px)' : 'translateY(0)',
      }}
    >
      <div className="flex items-center gap-2 w-full">
        <span className="shrink-0">{lane.icon}</span>
        <span className="text-sm font-semibold flex-1 truncate">{lane.label}</span>
        <span
          className="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full tabular-nums"
          style={{
            background: 'var(--color-surface-elevated)',
            color: 'var(--color-text-muted)',
            border: '1px solid var(--color-border)',
          }}
          title={`${count} list${count === 1 ? '' : 's'} already scheduled here`}
        >
          {count}
        </span>
      </div>
      <p className="text-[11px] text-[var(--color-text-muted)] leading-tight hidden sm:block">
        {lane.hint}
      </p>
      {visibleChips.length > 0 ? (
        <div className="flex flex-wrap gap-1 mt-1">
          {visibleChips.map((row) => (
            <ScheduleMiniChip
              key={`${row.kind}:${row.id}`}
              row={row}
              projectsById={projectsById}
            />
          ))}
          {hiddenCount > 0 ? (
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium"
              style={{
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-muted)',
                border: '1px solid var(--color-border)',
              }}
            >
              +{hiddenCount} more
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ScheduleMiniChip({
  row,
  projectsById,
}: {
  row: ScheduleZoneRow;
  projectsById?: Map<string, TodoProject>;
}) {
  // Use a distinct drag id prefix so dnd-kit doesn't conflict with the same
  // card's draggable shell over in the project Kanban column. The parent's
  // `parseWorkspaceDragId` recognises both prefixes.
  const dragId =
    row.kind === 'task'
      ? `ws-drag-zone:task:${row.task.id}`
      : `ws-drag-zone:focus:${row.focus.id}`;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: dragId,
  });

  const projectId = row.task.projectId;
  const project = projectId && projectsById ? projectsById.get(projectId) ?? null : null;
  const tokens = project?.colorKey ? TODO_PROJECT_COLOR_TOKENS[project.colorKey] : null;

  return (
    <button
      ref={setNodeRef}
      type="button"
      {...attributes}
      {...listeners}
      style={{
        transform: transform ? CSS.Transform.toString(transform) : undefined,
        opacity: isDragging ? 0.5 : 1,
        background: tokens?.tint ?? 'var(--color-surface-elevated)',
        color: tokens?.fg ?? 'var(--color-text-primary)',
        border: `1px solid ${tokens?.fg ?? 'var(--color-border)'}`,
        maxWidth: 160,
      }}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium cursor-grab active:cursor-grabbing transition-opacity touch-none"
      title={row.task.title}
    >
      {tokens ? (
        <span
          aria-hidden
          style={{
            width: 6,
            height: 6,
            borderRadius: 999,
            background: tokens.fg,
            flexShrink: 0,
          }}
        />
      ) : null}
      <span className="truncate">{row.task.title}</span>
    </button>
  );
}

function UnscheduleZone({ dragging }: { dragging: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'ws-unschedule' });

  let background = 'var(--color-surface)';
  let borderColor: string = 'var(--color-border)';
  let borderStyle: 'solid' | 'dashed' = 'solid';
  let foreground = 'var(--color-text-muted)';
  let opacity = 0.55;

  if (isOver) {
    background = 'rgba(215, 0, 151, 0.10)'; // matches --color-error tint
    borderColor = 'var(--color-error)';
    borderStyle = 'dashed';
    foreground = 'var(--color-error)';
    opacity = 1;
  } else if (dragging) {
    background = 'var(--color-surface-elevated)';
    borderColor = 'var(--color-text-muted)';
    borderStyle = 'dashed';
    foreground = 'var(--color-text-secondary)';
    opacity = 1;
  }

  return (
    <div
      ref={setNodeRef}
      role="region"
      aria-label="Drop here to remove from any schedule"
      className="flex flex-col items-start justify-center gap-1 px-3 py-2.5 rounded-xl transition-all"
      style={{
        background,
        border: `1.5px ${borderStyle} ${borderColor}`,
        color: foreground,
        opacity,
        transform: isOver ? 'translateY(-1px)' : 'translateY(0)',
      }}
    >
      <div className="flex items-center gap-2 w-full">
        <CircleSlash size={16} className="shrink-0" />
        <span className="text-sm font-semibold flex-1 truncate">Unschedule</span>
      </div>
      <p className="text-[11px] leading-tight hidden sm:block">
        {isOver ? 'Drop to remove from all lanes' : 'Drop a chip here to clear its lane'}
      </p>
    </div>
  );
}
